import React, { useState } from 'react';
import { Card, Radio, Button, Space, Modal, message, Typography, Alert, Divider } from 'antd';
import { ExclamationCircleOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../utils/api';

const { Title, Paragraph, Text } = Typography;

type ResetLevel = 'devices' | 'devices_categories' | 'devices_categories_policies' | 'all';

const ResetData: React.FC = () => {
  const [resetLevel, setResetLevel] = useState<ResetLevel>('devices');
  const [loading, setLoading] = useState(false);

  const resetOptions = [
    {
      value: 'devices' as ResetLevel,
      label: '只清空设备列表',
      description: '清空所有设备数据，保留目录、管控措施、版本等配置',
      color: '#52c41a',
    },
    {
      value: 'devices_categories' as ResetLevel,
      label: '清空设备列表、目录列表',
      description: '清空设备和目录数据，保留管控措施、版本等配置',
      color: '#faad14',
    },
    {
      value: 'devices_categories_policies' as ResetLevel,
      label: '清空设备列表、目录列表、管控措施列表',
      description: '清空设备、目录、管控措施数据，保留版本配置',
      color: '#ff7a45',
    },
    {
      value: 'all' as ResetLevel,
      label: '清空所有数据',
      description: '清空所有数据，包括设备、目录、管控措施、版本（不包括用户数据）',
      color: '#ff4d4f',
    },
  ];

  const handleReset = () => {
    const selectedOption = resetOptions.find(opt => opt.value === resetLevel);
    
    Modal.confirm({
      title: '确认重置数据',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <Paragraph>
            您确定要执行以下操作吗？
          </Paragraph>
          <Alert
            message={selectedOption?.label}
            description={selectedOption?.description}
            type="warning"
            showIcon
          />
          <Paragraph style={{ marginTop: 16, color: '#ff4d4f' }}>
            <strong>警告：此操作不可恢复！请确保您已经备份了重要数据。</strong>
          </Paragraph>
        </div>
      ),
      okText: '确认重置',
      okType: 'danger',
      cancelText: '取消',
      width: 520,
      onOk: async () => {
        setLoading(true);
        try {
          const response = await api.post('/reset-data', { level: resetLevel });
          message.success(response.data.message || '数据重置成功');
          
          // 显示详细信息
          if (response.data.details) {
            Modal.info({
              title: '重置完成',
              content: (
                <div>
                  <Paragraph>以下数据已被清空：</Paragraph>
                  <ul>
                    {response.data.details.map((detail: string, index: number) => (
                      <li key={index}>{detail}</li>
                    ))}
                  </ul>
                </div>
              ),
            });
          }
        } catch (error: any) {
          message.error(error.response?.data?.error || '重置数据失败');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <DeleteOutlined /> 重置数据
      </Title>
      <Paragraph type="secondary">
        为新客户部署时清空测试数据，保留必要的配置信息
      </Paragraph>

      <Card style={{ marginTop: 24 }}>
        <Alert
          message="重要提示"
          description="此功能用于新客户部署时清理脏数据。请谨慎操作，确保在正确的环境中执行。建议在重置前备份数据库文件。"
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 24 }}
        />

        <Divider>选择重置级别</Divider>

        <Radio.Group
          value={resetLevel}
          onChange={(e) => setResetLevel(e.target.value)}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {resetOptions.map((option) => (
              <Card
                key={option.value}
                hoverable
                style={{
                  borderColor: resetLevel === option.value ? option.color : undefined,
                  borderWidth: resetLevel === option.value ? 2 : 1,
                }}
              >
                <Radio value={option.value}>
                  <Space direction="vertical" size={4}>
                    <Text strong style={{ fontSize: 16 }}>
                      {option.label}
                    </Text>
                    <Text type="secondary">{option.description}</Text>
                  </Space>
                </Radio>
              </Card>
            ))}
          </Space>
        </Radio.Group>

        <Divider />

        <Space style={{ marginTop: 16 }}>
          <Button
            type="primary"
            danger
            size="large"
            icon={<DeleteOutlined />}
            onClick={handleReset}
            loading={loading}
          >
            执行重置
          </Button>
          <Button
            size="large"
            icon={<ReloadOutlined />}
            onClick={() => window.location.reload()}
          >
            刷新页面
          </Button>
        </Space>
      </Card>

      <Card style={{ marginTop: 24 }} title="数据重置说明">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Title level={5}>1. 只清空设备列表</Title>
            <Paragraph>
              • 删除所有设备记录<br />
              • 保留：目录、管控措施、版本、用户
            </Paragraph>
          </div>

          <div>
            <Title level={5}>2. 清空设备列表、目录列表</Title>
            <Paragraph>
              • 删除所有设备记录<br />
              • 删除所有目录记录<br />
              • 保留：管控措施、版本、用户
            </Paragraph>
          </div>

          <div>
            <Title level={5}>3. 清空设备列表、目录列表、管控措施列表</Title>
            <Paragraph>
              • 删除所有设备记录<br />
              • 删除所有目录记录<br />
              • 删除所有管控措施记录<br />
              • 保留：版本、用户
            </Paragraph>
          </div>

          <div>
            <Title level={5}>4. 清空所有数据</Title>
            <Paragraph>
              • 删除所有设备记录<br />
              • 删除所有目录记录<br />
              • 删除所有管控措施记录<br />
              • 删除所有版本记录<br />
              • 保留：用户账户
            </Paragraph>
          </div>

          <Alert
            message="注意"
            description="所有重置级别都不会删除用户账户数据，以保证管理员可以继续登录系统。"
            type="info"
            showIcon
          />
        </Space>
      </Card>
    </div>
  );
};

export default ResetData;
